import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store';
import { CursorType } from '../../../configs/constant';

export interface ReportItem {
  id: string;
  timestamp: string;
  type: string;
  title: string;
  message: string;
  location_id: string;
  gateway_serial: string;
  gateway_type_id: string;
  status: string;
  supervisor?: string;
}

export interface ReportFilters {
  supervisor: string;
  deviceType: string;
  diaryType: string;
  from: string | number;
  to: string | number;
  province: string;
}

interface ReportState {
  reports: ReportItem[];
  cursor: CursorType;
  limit: number;
  total: number;
  filters: ReportFilters;
  loading: boolean;
  error: string | null;
}

const initialState: ReportState = {
  reports: [],
  cursor: { after: null, before: null }, 
  limit: 10,
  total: 0,
  filters: {
    supervisor: 'all',
    deviceType: 'all',
    diaryType: 'all',
    from: '',
    to: '',
    province: '',
  },
  loading: false,
  error: null,
};

const slice = createSlice({
  name: 'reportState',
  initialState,
  reducers: {
    setReports: (
      state,
      { payload: { reports, cursor, total } }: PayloadAction<{
        reports: ReportItem[];
        cursor: CursorType;
        total: number;
      }>
    ) => {
      state.reports = reports;
      state.cursor = cursor;
      if (typeof total === 'number') {
        state.total = total;
      }
      state.loading = false;
      state.error = null;
    },
    setLimit: (state, { payload: { limit } }: PayloadAction<{ limit: number }>) => {
      state.limit = limit;
    },
    setFilters: (state, action: PayloadAction<ReportFilters>) => {
      state.filters = action.payload;
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setCursor: (state, action: PayloadAction<CursorType>) => {
      state.cursor = action.payload;
    },
  },
});

export const {
  setReports,
  setLimit,
  setFilters,
  resetFilters,
  setLoading,
  setError,
  setCursor, 
} = slice.actions;

export default slice.reducer;

export const selectReportState = (state: RootState) => state.reportState;
export const selectReports = (state: RootState) => state.reportState.reports;
export const selectReportCursor = (state: RootState) => state.reportState.cursor;
export const selectReportLimit = (state: RootState) => state.reportState.limit;
export const selectReportFilters = (state: RootState) => state.reportState.filters;
export const selectReportLoading = (state: RootState) => state.reportState.loading;
export const selectReportError = (state: RootState) => state.reportState.error;